/* Copyright (c) Trimble Inc. All rights reserved. */

import { Component, ErrorInfo, ReactNode } from 'react';

export interface ErrorBoundaryProps {
  /** Content to render when no error is caught. */
  children: ReactNode;
  /**
   * Optional fallback UI to display when an error is caught.
   * Receives the caught error and a reset callback.
   * Defaults to rendering nothing (silently swallows the crash so the rest
   * of the app keeps working — useful for non-critical widgets like the
   * session-expiry dialog).
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Optional callback invoked whenever an error is caught. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary
 *
 * Generic class component error boundary for use across the design system and
 * consuming applications.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary fallback={(err, reset) => <button onClick={reset}>Retry</button>}>
 *   <SomePotentiallyUnstableWidget />
 * </ErrorBoundary>
 * ```
 *
 * When no `fallback` prop is provided the boundary silently swallows the error
 * and renders nothing — ideal for non-critical UI like the session-expiry
 * dialog, where a crash should not take down the whole app.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.onError?.(error, errorInfo);
  }

  reset(): void {
    this.setState({ hasError: false, error: null });
  }

  override render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      return this.props.fallback ? this.props.fallback(this.state.error, this.reset) : null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
